import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import JwtDecode from "jwt-decode";
import * as Moment from "moment";

import { APIResults, AssemblyConfig } from "./sharedinterfaces";
import { lastValueFrom } from "rxjs";
import * as moment from "moment";
declare const BrandVars;

@Injectable()
export class ApiService {
	public token?: string;
	public user?: APIResults.User;

	constructor(private http: HttpClient) { }

	public IsLoggedIn(): boolean {
		return this.token != null;
	}

	private GetAPIRoot(): string {
		return BrandVars.apiRoot;
	}

	private async apiGet(route: string, authed?: boolean): Promise<any> {
		let headers = new HttpHeaders();
		if (authed) {
			if (this.token) {
				headers = headers.append("Authorization", "Bearer " + this.token);
			} else {
				throw new Error("No token available for API auth");
			}
		}
		return await lastValueFrom(this.http.get(this.GetAPIRoot() + route, { headers: headers }));
	}

	private async apiGetText(route: string, authed?: boolean): Promise<any> {
		let headers = new HttpHeaders();
		if (authed) {
			if (this.token) {
				headers = headers.append("Authorization", "Bearer " + this.token);
			} else {
				throw new Error("No token available for API auth");
			}
		}
		return await lastValueFrom(this.http.get(this.GetAPIRoot() + route, { headers: headers, responseType: "text" }));
	}

	private async apiGetBlob(route: string, authed?: boolean): Promise<Blob> {
		let headers = new HttpHeaders();
		if (authed) {
			if (this.token) {
				headers = headers.append("Authorization", "Bearer " + this.token);
			} else {
				throw new Error("No token available for API auth");
			}
		}
		return await lastValueFrom(this.http.get(this.GetAPIRoot() + route, { headers: headers, responseType: "blob" }));
	}

	private async apiPost(route: string, body: any, authed?: boolean): Promise<any> {
		let headers = new HttpHeaders();
		if (authed) {
			if (this.token) {
				headers = headers.append("Authorization", "Bearer " + this.token);
			} else {
				throw new Error("No token available for API auth");
			}
		}
		return await lastValueFrom(this.http.post(this.GetAPIRoot() + route, body, { headers: headers }));
	}

	private async apiUpload(route: string, file: File, authed?: boolean): Promise<any> {
		let headers = new HttpHeaders();
		headers = headers.append("Content-Type", "application/octet-stream");
		if (authed) {
			if (this.token) {
				headers = headers.append("Authorization", "Bearer " + this.token);
			} else {
				throw new Error("No token available for API auth");
			}
		}
		return await lastValueFrom(this.http.post(this.GetAPIRoot() + route, file, { headers: headers }));
	}

	private async apiDelete(route: string, authed?: boolean): Promise<any> {
		let headers = new HttpHeaders();
		if (authed) {
			if (this.token) {
				headers = headers.append("Authorization", "Bearer " + this.token);
			} else {
				throw new Error("No token available for API auth");
			}
		}
		return await lastValueFrom(this.http.delete(this.GetAPIRoot() + route, { headers: headers }));
	}

	public DownloadFile(filename: string, data: any) {
		const url = window.URL.createObjectURL(new Blob([data]));
		const link = document.createElement("a");
		link.href = url;
		link.setAttribute("download", filename);
		document.body.appendChild(link);
		link.click();
	}

	LoadTokenIfPossible(): void {
		try {
			let token = sessionStorage.getItem("token");
			if (!token) {
				token = localStorage.getItem("token");
			}
			if (token) {
				const decoded: any = JwtDecode(token);
				// Make sure it has the required properties
				if (decoded.user && decoded.user.email && decoded.user.user_id && decoded.user.role) {
					this.user = decoded.user;
					this.token = token;
				}
			}
		} catch (err) {
			console.error("Couldn't load stored token");
			console.error(err);
		}
	}

	async GitVersion(): Promise<string> {
		const result = await this.apiGet("/gitversion");
		return result.version;
	}

	async Login(email: string, password: string, rememberme: boolean): Promise<void> {
		const login = await this.apiPost("/auth/user", { email: email, password: password })
		if (!login.token) {
			throw new Error("No token returned from login");
		}
		const decoded: any = JwtDecode(login.token);
		this.user = decoded.user;
		this.token = login.token;
		if (rememberme) {
			localStorage.setItem("token", login.token);
		} else {
			sessionStorage.setItem("token", login.token);
		}
	}

	Logout(): void {
		this.token = undefined;
		this.user = undefined;
		localStorage.removeItem("token");
		sessionStorage.removeItem("token");
	}

	// Admin routes
	GetSystemLog(start: number, limit: number,
		environment_name: string, ec2_instance: string, remote_ip: string, user_id: number, device_id: number, asset_id: number, status_code: number, http_method: string,
		path: string, min_response: number | undefined,
		ignore_myself: boolean, ignore_userid: number, ignore_users: boolean, ignore_devices: boolean, ignore_anonymous: boolean, ignore_system: boolean, ignore_import: boolean, ignore_status: number | undefined, only_exceptions: boolean, only_this_environment: boolean
	): Promise<APIResults.SystemLogPage> {
		let querystring = "/admin/log?start=" + start + "&limit=" + limit;
		if (environment_name) { querystring += "&environment_name=" + environment_name; }
		if (ec2_instance) { querystring += "&ec2_instance=" + ec2_instance; }
		if (remote_ip) { querystring += "&remote_ip=" + remote_ip; }
		if (user_id) { querystring += "&user_id=" + user_id; }
		if (device_id) { querystring += "&device_id=" + device_id; }
		if (asset_id) { querystring += "&asset_id=" + asset_id; }
		if (status_code) { querystring += "&status_code=" + status_code; }
		if (http_method) { querystring += "&http_method=" + http_method; }
		if (path) { querystring += "&path=" + path; }
		if (min_response) { querystring += "&min_response=" + min_response; }
		if (ignore_myself) { querystring += "&ignore_myself=" + ignore_myself; }
		if (ignore_userid) { querystring += "&ignore_userid=" + ignore_userid; }
		if (ignore_users) { querystring += "&ignore_users=" + ignore_users; }
		if (ignore_devices) { querystring += "&ignore_devices=" + ignore_devices; }
		if (ignore_anonymous) { querystring += "&ignore_anonymous=" + ignore_anonymous; }
		if (ignore_system) { querystring += "&ignore_system=" + ignore_system; }
		if (ignore_import) { querystring += "&ignore_import=" + ignore_import; }
		if (ignore_status) { querystring += "&ignore_status=" + ignore_status; }
		if (only_exceptions) { querystring += "&only_exceptions=" + only_exceptions; }
		if (only_this_environment) { querystring += "&only_this_environment=" + only_this_environment; }
		return this.apiGet(querystring, true).then(result => {
			result.rows = DateifyObjects(result.rows, "ts");
			return result;
		});
	}

	async GetFullTree(): Promise<APIResults.Organization[]> {
		return await this.apiGet("/admin/fulltree", true);
	}
	GetOrganizations(): Promise<APIResults.Organization[]> {
		return this.apiGet("/admin/organizations", true);
	}

	AddOrganization(name: string): Promise<void> {
		return this.apiPost("/admin/organizations", { name: name }, true);
	}
	GetAllAssets(): Promise<APIResults.Asset[]> {
		return this.apiGet("/admin/assets", true);
	}
	GetAllConfigs(): Promise<APIResults.AssetConfig[]> {
		return this.apiGet("/admin/assetconfigs", true);
	}
	GetConfigContent(asset_type_id: number): Promise<string> {
		return this.apiGetText("/admin/assetconfigs/" + asset_type_id, true);
	}
	UpdateConfig(asset_type_id: number, asset_type_config: string): Promise<void> {
		return this.apiPost("/admin/assetconfigs/" + asset_type_id, { asset_type_config }, true);
	}
	AddConfig(organization_id: number, content: string): Promise<any> {
		return this.apiPost("/admin/assetconfigs", { organization_id, asset_type_config: content }, true);
	}
	DeleteConfig(asset_type_id: number): Promise<void> {
		return this.apiDelete("/admin/assetconfigs/" + asset_type_id, true);
	}
	GetAllTemplates(): Promise<APIResults.ConfigTemplate[]> {
		return this.apiGet("/admin/configtemplates", true);
	}
	GetTemplateContent(template_id: number): Promise<string> {
		return this.apiGetText("/admin/configtemplates/" + template_id, true);
	}
	GetThresholdData(asset_type_id: number, assets: number[]): Promise<APIResults.ThresholdResult> {
		return this.apiPost("/admin/assetconfigs/" + asset_type_id + "/thresholds", { assets }, true);
	}
	GetAssetAcqCounts(asset_type_id: number, assets: number[]): Promise<number[]> {
		return this.apiPost("/admin/assetconfigs/" + asset_type_id + "/acqcounts", { assets }, true);
	}
	async GetUserLogins(): Promise<APIResults.UserLoginCounts[]> {
		return this.apiGet("/admin/userlogins", true);
	}
	UpdateTemplate(template_id: number, content: string): Promise<void> {
		return this.apiPost("/master/configtemplates/" + template_id, { content }, true);
	}
	AddTemplate(description: string, content: string): Promise<any> {
		return this.apiPost("/master/configtemplates", { description, content }, true);
	}
	DeleteTemplate(template_id: number): Promise<void> {
		return this.apiDelete("/master/configtemplates/" + template_id, true);
	}
	RecalculateStates(forceall: boolean) {
		return this.apiPost("/master/recalculatestates", { all: forceall }, true);
	}
	GenerateHIs(asset_id: number) {
		return this.apiPost("/admin/assets/" + asset_id + "/generatehi", {}, true);
	}
	RecalculateAssetStates(asset_id: number) {
		return this.apiPost("/admin/assets/" + asset_id + "/recalculate", {}, true);
	}
	GetTaskStatus(task_id: number): Promise<APIResults.TaskState> {
		return this.apiGet("/admin/tasks/" + task_id, true);
	}
	GetAllComponents(asset_id: number): Promise<AssemblyConfig.Component[]> {
		return this.apiGet("/admin/assets/" + asset_id + "/allcomponents", true);
	}
	GetOperationAcquisitions(operation_id: number, component_key: string, date?: Date): Promise<APIResults.Acquisition[]> {
		let suffix = "";
		if (date) {
			suffix = "/" + moment(date).format("YYYY-MM-DD");
		}
		return this.apiGet("/admin/operations/" + operation_id + "/components/" + component_key + "/acquisitions" + suffix, true);
	}
	DeleteAcquisition(acquisition_id: number) {
		return this.apiDelete("/admin/acquisitions/" + acquisition_id, true);
	}
	DeleteOperation(asset_id: number, operation_id: number) {
		return this.apiDelete("/admin/assets/" + asset_id + "/operations/" + operation_id, true);
	}
	WipeAsset(asset_id: number) {
		return this.apiDelete("/admin/assets/" + asset_id + "/data", true);
	}
	DeleteAsset(asset_id: number) {
		return this.apiDelete("/admin/assets/" + asset_id, true);
	}
	GetRTBCoefficients(asset_id: number, rotorId: string, operationbefore: number, operationafter: number, adjustment: number[][]): Promise<any> {
		return this.apiPost("/admin/assets/" + asset_id + "/rtbcoefficients?rotorid=" + encodeURIComponent(rotorId) +
			"&operationbefore=" + operationbefore + "&operationafter=" + operationafter, { adjustment }, true);
	}
	GetTrackCoefficients(asset_id: number, rotorId: string, operationbefore: number, operationafter: number, adjustment: number[][]): Promise<any> {
		return this.apiPost("/admin/assets/" + asset_id + "/trackcoefficients?rotorid=" + encodeURIComponent(rotorId) +
			"&operationbefore=" + operationbefore + "&operationafter=" + operationafter, { adjustment }, true);
	}
	GetFileListing(asset_id: number, bucket: string, path: string): Promise<APIResults.FileListing> {
		return this.apiGet("/admin/assets/" + asset_id + "/filelist/" + bucket + "/" + path, true);
	}
	async GetDataFiles(asset_id: number, filetype: string, start: number, limit: number): Promise<APIResults.DataFile[]> {
		let result = await this.apiGet("/admin/assets/" + asset_id + "/datafiles/" + filetype + "?start=" + start + "&limit=" + limit, true);
		result = DateifyObjects(result, "imported");
		return result;
	}
	async DownloadDataFile(asset_id: number, datafile_id: number, filename: string): Promise<void> {
		const result = await this.apiGetBlob("/user/assets/" + asset_id + "/files/" + datafile_id, true);
		this.DownloadFile(filename, result);
	}
	async GetFileDownload(asset_id: number, bucket: string, path: string, filename: string): Promise<void> {
		const result = await this.apiGetBlob("/admin/assets/" + asset_id + "/file/" + bucket + "/" + path, true);
		this.DownloadFile(filename, result);
	}
	async GetErrorFiles(asset_id: number, start: number, limit: number): Promise<APIResults.DataFile[]> {
		let result = await this.apiGet("/admin/assets/" + asset_id + "/errorfiles" + "?start=" + start + "&limit=" + limit, true);
		result = DateifyObjects(result, "imported");
		return result;
	}
	async UploadControlFile(asset_id: number, file: File): Promise<void> {
		await this.apiUpload("/admin/assets/" + asset_id + "/controlfiles/" + file.name, file, true);
	}

	async ExportFirmware(filename: string, ignore_org: number): Promise<void> {
		const result = await this.apiGetText("/admin/firmware" + (ignore_org ? "?ignore_org=" + ignore_org : ""), true);
		this.DownloadFile(filename, result);
	}

	// Org admin routes
	GetUsers(organization_id: number): Promise<APIResults.User[]> {
		return this.apiGet("/orgadmin/organizations/" + organization_id + "/users", true);
	}

	AddUser(organization_id: number, email: string, password: string, role: string): Promise<void> {
		return this.apiPost("/orgadmin/organizations/" + organization_id + "/users", { email: email, password: password, role: role }, true);
	}

	ChangeUser(organization_id: number, user_id: number, password?: string, role?: string, hide_rtb?: boolean | number, hide_fdm?: boolean | number, hide_mech?: boolean | number): Promise<void> {
		const options: any = {};
		if (password) { options.password = password; }
		if (role) { options.role = role; }
		if (hide_rtb !== undefined) options.hide_rtb = hide_rtb ? 1 : 0;
		if (hide_fdm !== undefined) options.hide_fdm = hide_fdm ? 1 : 0;
		if (hide_mech !== undefined) options.hide_mech = hide_mech ? 1 : 0;
		return this.apiPost("/orgadmin/organizations/" + organization_id + "/users/" + user_id, options, true);
	}

	SendTestEmail(organization_id: number, user_id: number): Promise<void> {
		return this.apiPost("/orgadmin/organizations/" + organization_id + "/users/" + user_id + "/testemail", undefined, true);
	}

	DeleteUser(organization_id: number, user_id: number): Promise<void> {
		return this.apiDelete("/orgadmin/organizations/" + organization_id + "/users/" + user_id, true);
	}

	GetAssets(organization_id: number): Promise<APIResults.Asset[]> {
		return this.apiGet("/orgadmin/organizations/" + organization_id + "/assets", true);
	}

	AddAsset(organization_id: number, fleet_id: number, asset_type_id: number, asset_name: string, obcu_serial_number: string): Promise<any> {
		return this.apiPost("/admin/organizations/" + organization_id + "/assets", {
			fleet_id: fleet_id, asset_type_id: asset_type_id, asset_name: asset_name, obcu_serial_number: obcu_serial_number
		}, true);
	}

	ResetAsset(asset_id: number): Promise<any> {
		return this.apiPost("/admin/assets/" + asset_id + "/reset", undefined, true);
	}

	async GetCoordinatorSerial(prefix: string): Promise<string> {
		const result = await this.apiGet("/admin/serial/" + prefix + "/free", true);
		return result.serial;
	}

	async SaveCoordinatorSerial(serial: string, params: any): Promise<void> {
		await this.apiPost("/admin/serial/" + serial, params, true);
	}

	GetOrgFleets(organization_id: number): Promise<APIResults.Fleet[]> {
		return this.apiGet("/orgadmin/organizations/" + organization_id + "/fleets", true);
	}

	AddOrgFleet(organization_id: number, fleet_name: string): Promise<void> {
		return this.apiPost("/orgadmin/organizations/" + organization_id + "/fleets", { fleet_name: fleet_name }, true);
	}

	async DeleteFleet(organization_id: number, fleet_id: number): Promise<void> {
		return this.apiDelete("/orgadmin/organizations/" + organization_id + "/fleets/" + fleet_id, true);
	}

	async UpdateAsset(organization_id: number, asset_id: number, fields: any): Promise<void> {
		return this.apiPost("/orgadmin/organizations/" + organization_id + "/assets/" + asset_id, fields, true);
	}

	async UpdateAssetAdmin(asset_id: number, fields: any): Promise<void> {
		return this.apiPost("/admin/assets/" + asset_id, fields, true);
	}

	async ChangeOrgFleet(organization_id: number, fleet_id: number, tz: number, hidden?: boolean): Promise<void> {
		const time_offset_hours = tz ? Number(tz) : null;
		const options: any = {
			time_offset_hours,
			hidden: hidden,
		};
		return this.apiPost("/orgadmin/organizations/" + organization_id + "/fleets/" + fleet_id, options, true);
	}

	async GetAssetConfigs(organization_id: number): Promise<APIResults.AssetConfig[]> {
		return this.apiGet("/orgadmin/organizations/" + organization_id + "/configs", true);
	}

	async GetAssetPartners(asset_id: number): Promise<APIResults.Organization[]> {
		return this.apiGet("/admin/assets/" + asset_id + "/partners", true);
	}
	async AddAssetPartner(asset_id: number, organization_id: number): Promise<void> {
		return this.apiPost("/admin/assets/" + asset_id + "/partners", { organization_id }, true);
	}
	async DeleteAssetPartner(asset_id: number, organization_id: number): Promise<void> {
		return this.apiDelete("/admin/assets/" + asset_id + "/partners/" + organization_id, true);
	}

	async UpdateAltEngine(asset_id: number, alt_engine: boolean): Promise<void> {
		return this.apiPost("/orgadmin/assets/" + asset_id + "/altengine", { alt_engine }, true);
	}

	GetOrgSystemLog(start: number, limit: number,
		environment_name: string, ec2_instance: string, remote_ip: string, user_id: number, device_id: number, asset_id: number, status_code: number, http_method: string,
		path: string, min_response: number | undefined,
		ignore_myself: boolean, ignore_userid: number, ignore_users: boolean, ignore_devices: boolean, ignore_anonymous: boolean, ignore_system: boolean, ignore_import: boolean, ignore_status: number | undefined, only_exceptions: boolean, only_this_environment: boolean
	): Promise<APIResults.SystemLogPage> {
		let querystring = "/orgadmin/log?start=" + start + "&limit=" + limit;
		if (environment_name) { querystring += "&environment_name=" + environment_name; }
		if (ec2_instance) { querystring += "&ec2_instance=" + ec2_instance; }
		if (remote_ip) { querystring += "&remote_ip=" + remote_ip; }
		if (user_id) { querystring += "&user_id=" + user_id; }
		if (device_id) { querystring += "&device_id=" + device_id; }
		if (asset_id) { querystring += "&asset_id=" + asset_id; }
		if (status_code) { querystring += "&status_code=" + status_code; }
		if (http_method) { querystring += "&http_method=" + http_method; }
		if (path) { querystring += "&path=" + path; }
		if (min_response) { querystring += "&min_response=" + min_response; }
		if (ignore_myself) { querystring += "&ignore_myself=" + ignore_myself; }
		if (ignore_userid) { querystring += "&ignore_userid=" + ignore_userid; }
		if (ignore_users) { querystring += "&ignore_users=" + ignore_users; }
		if (ignore_devices) { querystring += "&ignore_devices=" + ignore_devices; }
		if (ignore_anonymous) { querystring += "&ignore_anonymous=" + ignore_anonymous; }
		if (ignore_system) { querystring += "&ignore_system=" + ignore_system; }
		if (ignore_import) { querystring += "&ignore_import=" + ignore_import; }
		if (ignore_status) { querystring += "&ignore_status=" + ignore_status; }
		if (only_exceptions) { querystring += "&only_exceptions=" + only_exceptions; }
		if (only_this_environment) { querystring += "&only_this_environment=" + only_this_environment; }
		return this.apiGet(querystring, true).then(result => {
			result.rows = DateifyObjects(result.rows, "ts");
			return result;
		});
	}

	GetAssetSystemLog(asset_id: number, start: number, limit: number,
		remote_ip: string, status_code: number, http_method: string,
		path: string, only_exceptions: boolean
	): Promise<APIResults.SystemLogPage> {
		let querystring = "/orgadmin/assetlog/" + asset_id + "?start=" + start + "&limit=" + limit;
		if (remote_ip) { querystring += "&remote_ip=" + remote_ip; }
		if (status_code) { querystring += "&status_code=" + status_code; }
		if (http_method) { querystring += "&http_method=" + http_method; }
		if (path) { querystring += "&path=" + path; }
		if (only_exceptions) { querystring += "&only_exceptions=" + only_exceptions; }
		return this.apiGet(querystring, true).then(result => {
			result.rows = DateifyObjects(result.rows, "ts");
			return result;
		});
	}

	// User routes
	ForgotPassword(email: string): Promise<void> {
		return this.apiPost("/auth/forgot", { email: email }, false);
	}
	ResetPassword(email: string, code: string, newpassword: string): Promise<void> {
		return this.apiPost("/auth/reset", { email, code, newpassword }, false);
	}
	ChangePassword(oldpassword: string, newpassword: string): Promise<void> {
		return this.apiPost("/user/changepassword", { oldpassword: oldpassword, newpassword: newpassword }, true);
	}

	GetFleets(): Promise<APIResults.Fleet[]> {
		if (this.user.role === "admin" || this.user.role === "master") {
			return this.apiGet("/admin/fleets", true);
		} else {
			return this.apiGet("/user/fleets", true);
		}
	}

	async GetExceedances(asset_id: number, operation_id: number): Promise<APIResults.OpExceedances> {
		const exceedances = await this.apiGet("/user/assets/" + asset_id + "/exceedances/" + operation_id, true);
		exceedances.rows = DateifyObjects(exceedances.rows, "timestamp");
		return exceedances;
	}

	async UpdateOperationAck(asset_id: number, operation_id: number, ack: boolean): Promise<number> {
		const result = await this.apiPost("/user/assets/" + asset_id + "/operations/" + operation_id + "/ack", { ack }, true);
		return result.remaining;
	}

	async GetStates(asset_id: number, operation_id: number, columns: number[]): Promise<APIResults.StateResult> {
		return await this.apiGet("/user/assets/" + asset_id + "/states/" + operation_id + "?" + columns.map(item => "column=" + item).join("&"), true);
	}

	async ExportStates(filename: string, asset_id: number, operation_id: number): Promise<void> {
		const result = await this.apiGetText("/user/assets/" + asset_id + "/exportstates/" + operation_id, true);
		this.DownloadFile(filename, result);
	}

	async GetFlightPath(asset_id: number, operation_id: number): Promise<any[][]> {
		const result = await this.apiGet("/user/assets/" + asset_id + "/path/" + operation_id, true);
		// result = DateifyObjects(result, 0);
		return result;
	}

	async GetExpandedFlightPath(asset_id: number, operation_id: number): Promise<APIResults.FlightPathFrame[]> {
		const result = await this.apiGet("/user/assets/" + asset_id + "/flight/" + operation_id, true);
		// result = DateifyObjects(result, 0);
		return result;
	}

	GetRTBConfig(asset_id: number): Promise<{ rotors: APIResults.Rotor[], regimes: APIResults.RegimeIndex, check: APIResults.VibeCheck }> {
		return this.apiGet("/user/assets/" + asset_id + "/rtbconfig", true);
	}

	async GetVibeCheck(asset_id: number): Promise<APIResults.VibeCheckResultRow[]> {
		let result = await this.apiGet("/user/assets/" + asset_id + "/vibecheck", true);
		result = DateifyObjects(result, "date");
		return result;
	}

	GetOperations(asset_id: number, limit?: number, showEmpty?: boolean, onlyExceedances?: boolean): Promise<APIResults.Operation[]> {
		return this.apiGet("/user/assets/" + asset_id + "/operations" + (limit ? "?limit=" + limit : "") + (showEmpty ? "&empty=true" : "") + (onlyExceedances ? "&exceedances=true" : ""), true).then(result => {
			result = DateifyObjects(result, "start_time");
			result = DateifyObjects(result, "end_time");
			return result;
		});
	}

	async GetRTBOperations(asset_id: number, rotor_id: string, track: boolean, limit?: number): Promise<APIResults.Operation[]> {
		let result = await this.apiGet("/user/assets/" + asset_id + "/rtboperations?rotor_id=" + rotor_id + "&track=" + track + (limit ? "&limit=" + limit : ""), true);
		result = DateifyObjects(result, "start_time");
		result = DateifyObjects(result, "end_time");
		return result;
	}

	async GetOperationTotals(asset_id: number, start: string, end: string): Promise<APIResults.Operation[]> {
		return await this.apiGet("/user/assets/" + asset_id + "/operations/totals?start=" + start + "&end=" + end, true);
	}

	GetTrackOperations(asset_id: number, limit?: number, showEmpty?: boolean): Promise<APIResults.Operation[]> {
		return this.apiGet("/user/assets/" + asset_id + "/trackoperations" + (limit ? "?limit=" + limit : "") + (showEmpty ? "&empty=true" : ""), true).then(result => {
			result = DateifyObjects(result, "start_time");
			result = DateifyObjects(result, "end_time");
			return result;
		});
	}

	GetRegimeCounts(asset_id: number, rotorId: string, operationId: number): Promise<APIResults.RegimeCountsResult> {
		return this.apiGet("/user/assets/" + asset_id + "/regimecounts?rotorid=" +
			encodeURIComponent(rotorId) + "&operationid=" + operationId, true);
	}

	GetRTBData(asset_id: number, rotorId: string, operationId: number, regimes: number[], shaftOrder: number, adjustments: boolean[], flatTrack: boolean, onePerRevOnly: boolean): Promise<APIResults.RTBResults> {
		const regimeescape = regimes.map(item => "&regime[]=" + item).join("");
		const adjustmentescape = adjustments.map(item => "&adjustment[]=" + item).join("");
		const oneperrev = onePerRevOnly ? "&oneperrev=true" : "";
		return this.apiGet("/user/assets/" + asset_id + "/rtbdata?rotorid=" + encodeURIComponent(rotorId) +
			"&operationid=" + operationId + regimeescape + "&shaftorder=" + shaftOrder + "&flattrack=" + flatTrack + adjustmentescape + oneperrev, true);
	}

	GetRTBDataMultiple(asset_id: number, rotorId: string, operationIds: number[], regimes: number[], shaftOrder: number, adjustments: boolean[], flatTrack: boolean, onePerRevOnly: boolean): Promise<APIResults.RTBResults> {
		const operationescape = operationIds.map(item => "&operationid[]=" + item).join("");
		const regimeescape = regimes.map(item => "&regime[]=" + item).join("");
		const adjustmentescape = adjustments.map(item => "&adjustment[]=" + item).join("");
		const oneperrev = onePerRevOnly ? "&oneperrev=true" : "";
		return this.apiGet("/user/assets/" + asset_id + "/rtbdata?rotorid=" + encodeURIComponent(rotorId) +
			operationescape + regimeescape + "&shaftorder=" + shaftOrder + "&flattrack=" + flatTrack + adjustmentescape + oneperrev, true);
	}

	GetRTBManual(asset_id: number, rotorId: string, operationId: number, regimes: number[], shaftOrder: number, adjustments: boolean[], manualAdj: number[][], flatTrack: boolean, onePerRevOnly: boolean): Promise<APIResults.RTBResults> {
		const regimeescape = regimes.map(item => "&regime[]=" + item).join("");
		const adjustmentescape = adjustments.map(item => "&adjustment[]=" + item).join("");
		const oneperrev = onePerRevOnly ? "&oneperrev=true" : "";
		return this.apiPost("/user/assets/" + asset_id + "/rtbmanual?rotorid=" + encodeURIComponent(rotorId) +
			"&operationid=" + operationId + regimeescape + "&shaftorder=" + shaftOrder + "&flattrack=" + flatTrack + adjustmentescape + oneperrev, { manualAdj }, true);
	}

	GetRTBHistoryCounts(asset_id: number, rotorId: string, component: string, shaftOrder: number): Promise<APIResults.RegimeCount[]> {
		return this.apiGet("/user/assets/" + asset_id + "/rtbhistorycounts?rotorid=" + encodeURIComponent(rotorId) +
			"&component=" + component + "&shaftorder=" + shaftOrder, true);
	}

	GetRTBHistory(asset_id: number, rotorId: string, component: string, regimes: number[], shaftOrder: number): Promise<APIResults.RTBHistoryResults> {
		const regimeescape = regimes.map(item => "&regime[]=" + item).join("");
		return this.apiGet("/user/assets/" + asset_id + "/rtbhistory?rotorid=" + encodeURIComponent(rotorId) +
			"&component=" + component + regimeescape + "&shaftorder=" + shaftOrder, true);
	}

	GetTrackHistoryCounts(asset_id: number, rotorId: string): Promise<APIResults.RegimeCount[]> {
		return this.apiGet("/user/assets/" + asset_id + "/trackhistorycounts?rotorid=" + encodeURIComponent(rotorId), true);
	}

	GetTrackHistory(asset_id: number, rotorId: string, regimes: number[]): Promise<APIResults.TrackHistoryResult[]> {
		const regimeescape = regimes.map(item => "&regime[]=" + item).join("");
		return this.apiGet("/user/assets/" + asset_id + "/trackhistory?rotorid=" + encodeURIComponent(rotorId) + regimeescape, true);
	}

	GetAdjustmentHistory(asset_id: number, rotorId: string): Promise<APIResults.AdjustmentHistory[]> {
		return this.apiGet("/user/assets/" + asset_id + "/adjustmenthistory?rotorid=" + encodeURIComponent(rotorId), true);
	}

	UploadAdjustment(asset_id: number, rotorId: string, operationId: number, adjustments: string, userNote: string): Promise<void> {
		return this.apiPost("/user/assets/" + asset_id + "/adjustmenthistory?rotorid=" + encodeURIComponent(rotorId), { operationid: operationId, adjustments, usernote: userNote }, true);
	}

	DeleteAdjustment(asset_id: number, adjustment_history_id: number): Promise<void> {
		return this.apiDelete("/orgadmin/assets/" + asset_id + "/adjustmenthistory/" + adjustment_history_id, true);
	}

	GetMaintenanceHistory(asset_id: number): Promise<APIResults.MaintenanceHistory[]> {
		return this.apiGet("/user/assets/" + asset_id + "/maintenancehistory", true);
	}

	UploadMaintenance(asset_id: number, date: string, lru: number, userNote: string): Promise<void> {
		return this.apiPost("/user/assets/" + asset_id + "/maintenancehistory", { date, lru, usernote: userNote }, true);
	}

	DeleteMaintenance(asset_id: number, maintenance_history_id: number): Promise<void> {
		return this.apiDelete("/orgadmin/assets/" + asset_id + "/maintenancehistory/" + maintenance_history_id, true);
	}

	GetVibeCheckAcks(asset_id: number): Promise<APIResults.VibeCheckAck[]> {
		return this.apiGet("/user/assets/" + asset_id + "/vibecheckack", true);
	}

	UploadVibeCheckAck(asset_id: number, userNote: string): Promise<void> {
		return this.apiPost("/user/assets/" + asset_id + "/vibecheckack", { usernote: userNote }, true);
	}

	DeleteVibeCheckAck(asset_id: number, vibe_check_ack_id: number): Promise<void> {
		return this.apiDelete("/orgadmin/assets/" + asset_id + "/vibecheckack/" + vibe_check_ack_id, true);
	}

	GetAdminLogs(asset_id: number): Promise<any[]> {
		return this.apiGet("/admin/assets/" + asset_id + "/adminlogs", true);
	}

	CreateAdminLog(asset_id: number, title: string, content: string): Promise<void> {
		return this.apiPost("/admin/assets/" + asset_id + "/adminlogs", { title, content }, true);
	}

	UpdateAdminLog(admin_log_id: number, title: string, content: string): Promise<void> {
		return this.apiPost("/admin/adminlogs/" + admin_log_id, { title, content }, true);
	}

	DeleteAdminLog(admin_log_id: number): Promise<void> {
		return this.apiDelete("/admin/adminlogs/" + admin_log_id, true);
	}

	async GetFirmware(asset_id: number): Promise<APIResults.FirmwareResult> {
		const result = await this.apiGet("/user/assets/" + asset_id + "/firmware", true);
		if (result.ts) result.ts = Moment(result.ts).utc();
		return result;
	}

	async GetCommFailures(asset_id: number, start: number, limit: number): Promise<APIResults.CommFailurePage> {
		const result = await this.apiGet("/user/assets/" + asset_id + "/commfailures?start=" + start + "&limit=" + limit, true);
		result.rows = DateifyObjects(result.rows, "operation_start_time");
		result.rows = DateifyObjects(result.rows, "ts");
		return result;
	}

	async LogAssetAccess(asset_id: number): Promise<void> {
		await this.apiPost("/user/assets/" + asset_id + "/access", {}, true);
	}

	async GetAssetAccess(asset_id: number, start: number, limit: number): Promise<APIResults.AssetAccessPage> {
		const result = await this.apiGet("/user/assets/" + asset_id + "/access?start=" + start + "&limit=" + limit, true);
		result.rows = DateifyObjects(result.rows, "ts");
		return result;
	}

	async GetAlerts(asset_id: number, start: number, limit: number): Promise<APIResults.AlertPage> {
		const result = await this.apiGet("/user/assets/" + asset_id + "/alerts?start=" + start + "&limit=" + limit, true);
		result.rows = DateifyObjects(result.rows, "timestamp");
		result.rows = DateifyObjects(result.rows, "ack_timestamp");
		return result;
	}

	async DeleteAlert(asset_id: number, alert_id: number): Promise<void> {
		return this.apiDelete("/orgadmin/assets/" + asset_id + "/alerts/" + alert_id, true);
	}

	async UpdateAlertAck(asset_id: number, alert_id: number, note: string, ack: boolean): Promise<void> {
		await this.apiPost("/user/assets/" + asset_id + "/alerts/" + alert_id + "/ack", { ack, note }, true);
	}

	async ExportAlerts(filename: string, asset_id: number): Promise<void> {
		const result = await this.apiGetText("/user/assets/" + asset_id + "/alerts/export", true);
		this.DownloadFile(filename, result);
	}

	GetComponents(asset_id: number): Promise<APIResults.AssetComponentList> {
		return this.apiGet("/user/assets/" + asset_id + "/components", true).then((components: APIResults.AssetComponentList) => {
			// Move ghost shaft components to real shaft
			for (const ghostshaft of components.shaft) {
				if (ghostshaft.altid) {
					const realshaft = components.shaft.find(item => item.id === ghostshaft.altid);
					if (realshaft) {
						realshaft.gear.push(...ghostshaft.gear);
						realshaft.bearing.push(...ghostshaft.bearing);
					} else {
						console.log("No real shaft " + ghostshaft.altid + " for ghost shaft " + ghostshaft.id);
					}
				}
			}
			return components;
		});
	}

	GetRegimes(asset_id: number): Promise<any[] | undefined> {
		return this.apiGet("/user/assets/" + asset_id + "/regimes", true);
	}

	GetParameters(asset_id: number): Promise<AssemblyConfig.StateParameter[] | undefined> {
		return this.apiGet("/user/assets/" + asset_id + "/parameters", true);
	}

	GetComponentHi(asset_id: number, componentCode: string, start: number, limit: number, forceRul: boolean): Promise<APIResults.HiResult> {
		return this.apiGet("/user/assets/" + asset_id + "/components/" + encodeURIComponent(componentCode) + "/hi?start=" + start + "&limit=" + limit + "&force_rul=" + forceRul, true);
	}

	GetComponentCi(asset_id: number, componentCode: string, ciIndex: number, start: number, limit: number): Promise<APIResults.CiResult> {
		return this.apiGet("/user/assets/" + asset_id + "/components/" + encodeURIComponent(componentCode) + "/ci/" + ciIndex +
			"?start=" + start + "&limit=" + limit, true);
	}

	GetComponentRawPairDates(asset_id: number, componentCode: string): Promise<any[]> {
		return this.apiGet(
			"/user/assets/" + asset_id + "/components/" + encodeURIComponent(componentCode) + "/rawpairdates", true
		);
	}
	GetComponentRawPairs(asset_id: number, componentCode: string, start: string, end: string): Promise<APIResults.RawPairsResult[]> {
		return this.apiGet(
			"/user/assets/" + asset_id + "/components/" + encodeURIComponent(componentCode) + "/rawpairs?start=" + encodeURIComponent(start) + "&end=" + encodeURIComponent(end), true
		).then((result: APIResults.RawPairsResult[]) => {
			return DateifyObjects(result, "ts");
		});
	}

	GetComponentRawSingleDates(asset_id: number, componentCode: string): Promise<any[]> {
		return this.apiGet(
			"/user/assets/" + asset_id + "/components/" + encodeURIComponent(componentCode) + "/rawsingledates", true
		);
	}
	GetComponentRawSingles(asset_id: number, componentCode: string, start: string, end: string): Promise<APIResults.RawSinglesResult[]> {
		return this.apiGet(
			"/user/assets/" + asset_id + "/components/" + encodeURIComponent(componentCode) + "/rawsingles?start=" + encodeURIComponent(start) + "&end=" + encodeURIComponent(end), true
		).then((result: APIResults.RawSinglesResult[]) => {
			return DateifyObjects(result, "ts");
		});
	}

	async ExportRaw(filename: string, asset_id: number, raw_id: number, format: string): Promise<void> {
		const result = await this.apiGetBlob("/user/assets/" + asset_id + "/exportraw/" + raw_id + "?format=" + format, true);
		this.DownloadFile(filename, result);
	}

	GetShaftVibe(asset_id: number, componentCode: string, tachrawid: number, viberawid: number, useorder: boolean): Promise<APIResults.VibeShaftResult> {
		return this.apiGet(
			"/user/assets/" + asset_id + "/components/" + encodeURIComponent(componentCode) + "/vibe/shaft?tachrawid=" + tachrawid +
			"&viberawid=" + viberawid + "&useorder=" + useorder, true
		).then((result: APIResults.VibeShaftResult) => {
			result.tsa = result.tsa.map((value, index) => {
				return [(index + 1) / result.tsa.length, value];
			});
			result.fft = result.fft.map((value, index) => {
				return [(index + 0) * result.dfrqtsa, value];
			});
			result.spectrum = result.spectrum.map((value, index) => {
				return [(index + 0) * result.dfrqspec, value];
			});
			return result;
		});
	}

	GetGearVibe(asset_id: number, componentCode: string, tachrawid: number, viberawid: number): Promise<APIResults.VibeGearResult> {
		return this.apiGet(
			"/user/assets/" + asset_id + "/components/" + encodeURIComponent(componentCode) + "/vibe/gear?tachrawid=" + tachrawid +
			"&viberawid=" + viberawid, true
		).then((result: APIResults.VibeGearResult) => {
			result.res = result.res.map((value, index) => {
				return [(index + 1) / result.res.length, value];
			});
			result.amres = result.amres.map((value, index) => {
				return [(index + 1) / result.amres.length, value];
			});
			result.am = result.am.map((value, index) => {
				return [(index + 1) / result.am.length, value];
			});
			result.fm = result.fm.map((value, index) => {
				return [(index + 1) / result.fm.length, value];
			});
			result.eo = result.eo.map((value, index) => {
				return [(index + 1) / result.eo.length, value];
			});
			return result;
		});
	}

	GetBearingVibe(asset_id: number, componentCode: string, tachrawid: number, viberawid: number, useorder: boolean): Promise<APIResults.VibeBearingResult> {
		return this.apiGet("/user/assets/" + asset_id + "/components/" + encodeURIComponent(componentCode) + "/vibe/bearing?tachrawid=" + tachrawid + "&viberawid=" + viberawid + "&useorder=" + useorder, true).then((result: APIResults.VibeBearingResult) => {
			ScaleSpectrum(result);
			return result;
		});
	}

	GetTachVibe(asset_id: number, componentCode: string, tachrawid: number): Promise<APIResults.VibeTachResult> {
		return this.apiGet("/user/assets/" + asset_id + "/components/" + encodeURIComponent(componentCode) + "/vibe/tach?tachrawid=" + tachrawid, true).then((result: APIResults.VibeTachResult) => {
			result.tsa = result.tsa.map((value, index) => {
				return [(index + 1) / result.tsa.length, value];
			});
			result.fft = result.fft.map((value, index) => {
				return [(index + 1) / result.fft.length, value];
			});
			return result;
		});
	}

	async GetSensorVibe(asset_id: number, componentCode: string, rawid: number): Promise<APIResults.SensorDetailResult> {
		const result: APIResults.SensorDetailResult = await this.apiGet("/user/assets/" + asset_id + "/components/" + encodeURIComponent(componentCode) + "/vibe/sensor?rawid=" + rawid, true);
		result.spectrum = result.spectrum.map((value, index) => {
			return [(index + 0) * result.dfrqspec, value];
		});
		return result;
	}

	GetMotorShaftDetail(asset_id: number, componentCode: string, rawid: number): Promise<APIResults.MotorDetailResult> {
		return this.apiGet("/user/assets/" + asset_id + "/components/" + encodeURIComponent(componentCode) + "/electric/motorshaft?rawid=" + rawid, true).then((result: APIResults.MotorDetailResult) => {
			ScaleSpectrum(result);
			return result;
		});
	}

	GetMotorDetail(asset_id: number, componentCode: string, rawid: number): Promise<APIResults.MotorDetailResult> {
		return this.apiGet("/user/assets/" + asset_id + "/components/" + encodeURIComponent(componentCode) + "/electric/motor?rawid=" + rawid, true).then((result: APIResults.MotorDetailResult) => {
			ScaleSpectrum(result);
			return result;
		});
	}

	GetMotorBearingDetail(asset_id: number, componentCode: string, rawid: number): Promise<APIResults.MotorBearingDetailResult> {
		return this.apiGet("/user/assets/" + asset_id + "/components/" + encodeURIComponent(componentCode) + "/electric/motorbearing?rawid=" + rawid, true).then((result: APIResults.MotorBearingDetailResult) => {
			ScaleSpectrum(result);
			return result;
		});
	}

	GetCurrentDetail(asset_id: number, componentCode: string, rawid: number): Promise<APIResults.CurrentDetailResult> {
		return this.apiGet("/user/assets/" + asset_id + "/components/" + encodeURIComponent(componentCode) + "/electric/current?rawid=" + rawid, true).then((result: APIResults.CurrentDetailResult) => {
			ScaleSpectrum(result);
			return result;
		});
	}

	GetImpactDetail(asset_id: number, componentCode: string, rawid: number): Promise<APIResults.ImpactDetailResult> {
		return this.apiGet("/user/assets/" + asset_id + "/components/" + encodeURIComponent(componentCode) + "/electric/impact?rawid=" + rawid, true).then((result: APIResults.ImpactDetailResult) => {
			ScaleImpact(result);
			return result;
		});
	}

	GetNotifyAssets(): Promise<APIResults.NotificationAsset[]> {
		return this.apiGet("/user/notifyassets", true);
	}

	SetNotifyAssets(assets: any[]): Promise<void> {
		return this.apiPost("/user/notifyassets", { assets }, true);
	}

	async GetSMSNumber(): Promise<string> {
		const result = await this.apiGet("/user/smsnumber", true);
		return result.sms_number;
	}

	async SetSMSNumber(sms_number: string): Promise<void> {
		await this.apiPost("/user/smsnumber", { sms_number }, true);
	}

	async GetNotificationOptions(): Promise<any> {
		return this.apiGet("/user/notificationoptions", true);
	}

	async SetNotificationOptions(mute_imports: boolean, mute_commfails: boolean): Promise<void> {
		await this.apiPost("/user/notificationoptions", { mute_imports, mute_commfails }, true);
	}

	async GetFleetCompare(asset_id: number, component_key: string): Promise<APIResults.FleetComparison[]> {
		return this.apiGet("/user/assets/" + asset_id + "/fleetcompare/" + component_key, true);
	}

	async GetUserSupport(): Promise<any> {
		return this.apiGet("/user/support", true);
	}
	async SendSupportRequest(content: string): Promise<any> {
		return this.apiPost("/user/support", { content }, true);
	}
	async GetKnowledgeBase(): Promise<any> {
		return this.apiGet("/user/knowledge", true);
	}
}

function ScaleSpectrum(result: any) {
	result.spectrum = result.spectrum.map((value: number, index: number) => {
		return [index * result.dfreq + result.dfreq / 2.0, value];
	});
}

function ScaleImpact(result: any) {
	const SAMPLE_RATE = 31.25;
	result.spectrumx = result.spectrumx.map((value: number, index: number) => {
		return [index / SAMPLE_RATE, value];
	});
	result.spectrumy = result.spectrumy.map((value: number, index: number) => {
		return [index / SAMPLE_RATE, value];
	});
	result.spectrumz = result.spectrumz.map((value: number, index: number) => {
		return [index / SAMPLE_RATE, value];
	});
}

function DateifyObjects(items: any[], field?: string | number) {
	if (field) {
		return items.map(item => {
			item[field] = Moment(item[field]).utc();
			return item;
		});
	} else {
		return items.map(item => Moment(item).utc());
	}
}

